import { useEffect, useState } from "react";

import { useRootStore } from "store";
import { CustomerDocument } from "models/customers/documents";
import { SortIcon } from "components/icon/Icon";
import { SortOrder } from "common/SortOrder";
import { Loader } from "components/loader/Loader";
import { DocumentsFilters } from "../components/DocumentsFilters";
import { DocumentsTableRow } from "../components/DocumentsTableRow";

import "./Documents.scss";

type HeaderItem = {
  title: string;
  hasSort?: boolean;
  sortOption?: keyof SortingOptions;
};
export type SortingOptions = {
  sortByDateOrder: string;
  sortByCategoryOrder: string;
};

export function Documents() {
  const { fetchCustomerDocuments, fetchDocumentsCount } = useRootStore(
    (state) => state.customers
  );

  const { organizationNumber, isLoading } = useRootStore(
    (state) => state.customers.workingCustomer
  );

  const {
    data,
    total,
    isLoading: isLoadingDocuments,
    currentPage,
    pageSize
  } = useRootStore((state) => state.customers.workingCustomer.documents);

  const [sortingOptions, setSortingOptions] = useState<SortingOptions>({
    sortByDateOrder: "",
    sortByCategoryOrder: ""
  });
  const [documentsToShow, setDocumentsToShow] = useState<CustomerDocument[]>();

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const tableHeaderItems: HeaderItem[] = [
    { title: "Datum", hasSort: true, sortOption: "sortByDateOrder" },
    { title: "Tid" },
    { title: "Filer" },
    { title: "" },
    { title: "Kategori" },
    { title: "Från" },
    { title: "Åtgärder" }
  ];

  useEffect(() => {
    if (data?.skickaDocuments && total && total > 0) {
      const documentsOnCurrentPage = data.skickaDocuments.slice(
        startIndex,
        endIndex
      );
      setDocumentsToShow(documentsOnCurrentPage);
    }
  }, [data?.skickaDocuments, endIndex, startIndex, total]);

  useEffect(() => {
    if (organizationNumber && !total) {
      fetchDocumentsCount(organizationNumber);
    }
    if (organizationNumber && !!total) {
      fetchCustomerDocuments(organizationNumber, {
        pageSize: pageSize + 1
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationNumber, total]);

  const setSortingOrder = (sortOption?: keyof SortingOptions) => {
    if (sortOption) {
      const isAscending = sortingOptions[sortOption] === SortOrder.Ascending;
      setSortingOptions((prev) => ({
        ...prev,
        [sortOption as string]: isAscending
          ? SortOrder.Descending
          : SortOrder.Ascending
      }));
    }
  };

  const renderHeaderItem = (item: HeaderItem) =>
    item.hasSort ? (
      <th key={item.title}>
        <div className="d-flex">
          {item.title}
          <SortIcon
            className="ml-2"
            sortOrder={item.sortOption ? sortingOptions[item.sortOption] : ""}
            onClick={() => setSortingOrder(item.sortOption)}
          />
        </div>
      </th>
    ) : (
      <th key={item.title}>{item.title}</th>
    );

  return (
    <div className="box">
      <div>
        <div className="d-flex align-items-center">
          <h1 className="my-0 mr-2">Dokument</h1>
          <span className="text-gray mr-sm">
            (Totalt antal dokument: {total})
          </span>
        </div>
        <hr className="header-border" />
        <DocumentsFilters sortingOptions={sortingOptions} />
      </div>
      {isLoadingDocuments || isLoading ? (
        <Loader />
      ) : (
        <div className="table-wrapper documents-table">
          <table>
            <thead>
              <tr>{tableHeaderItems.map((item) => renderHeaderItem(item))}</tr>
            </thead>
            <tbody>
              {documentsToShow && documentsToShow.length > 0 ? (
                documentsToShow.map((dataItem, index) => (
                  <DocumentsTableRow
                    dataItem={dataItem}
                    key={`${startIndex}-${index}`}
                  />
                ))
              ) : (
                <tr className="mt-4">
                  <td>Inga dokument hittades</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
